import { parseSseFrames } from './events'
import type { Frame } from './events'

/**
 * The user-scoped list stream. `04 §3.6` — M-5.
 *
 * <p><b>ONE HELD EXECUTION PER VIEWER, NOT N.</b> A person watching eight engagements opens ONE
 * stream carrying `state` and a reduced `progress` for the engagements they own. Opening one per
 * row would cost a Function execution per engagement to render a list that shows one line each,
 * which is what keeps the Flex Consumption budget honest.</p>
 *
 * <p>It is deliberately much simpler than the engagement stream: there is NO BACKLOG and no `seq`
 * to resume from. The list itself is a paged query the page has just run, and replaying every owned
 * engagement's transitions would re-send what is already on the screen.</p>
 */
export class ListStream {
  private readonly token: () => Promise<string>
  private readonly onFrame: (frame: Frame) => void
  private readonly onStatus: (connected: boolean) => void
  private abort: AbortController | null = null
  private stopped = false
  private failures = 0
  private unauthorised = 0

  /**
   * @param token A fresh access token per request.
   * @param onFrame One reduced transition.
   * @param onStatus
   *   Whether the stream is CONNECTED. It is not derivable from the frames: an owner with nothing
   *   happening receives none for as long as nothing happens, so a page that lit the indicator on
   *   the first frame showed "reconnecting" indefinitely on a healthy connection — which is the
   *   one thing the indicator exists to distinguish.
   */
  constructor(token: () => Promise<string>, onFrame: (frame: Frame) => void,
              onStatus: (connected: boolean) => void = () => {}) {
    this.token = token
    this.onFrame = onFrame
    this.onStatus = onStatus
  }

  async start(): Promise<void> {
    while (!this.stopped) {
      const clean = await this.once()
      if (this.stopped) break

      if (clean) this.failures = 0
      await sleep(clean ? 250 : this.backoff())
    }
  }

  stop(): void {
    this.stopped = true
    this.abort?.abort()
  }

  private async once(): Promise<boolean> {
    this.abort = new AbortController()

    try {
      const response = await fetch('/api/engagements/events', {
        headers: { Authorization: `Bearer ${await this.token()}` },
        signal: this.abort.signal,
        cache: 'no-store',
      })

      if (response.status === 401) {
        // ONE 401 IS AN EXPIRED TOKEN AND IS WORTH COMING STRAIGHT BACK FOR. A SECOND IN A ROW IS
        // NOT: something is refusing us, and reconnecting every 250 ms turns that into four
        // requests a second against a Function App for as long as the tab is open. The first is
        // treated as a clean recycle, the rest as failures so the backoff applies.
        this.unauthorised += 1
        return this.unauthorised <= 1
      }
      if (!response.ok || !response.body) return false

      this.unauthorised = 0
      this.onStatus(true)

      for await (const frame of parseSseFrames(response.body)) {
        if (frame.kind === 'recycle') return true
        this.onFrame(frame)
      }

      return true
    } catch {
      return this.stopped
    } finally {
      this.abort = null
      this.onStatus(false)
    }
  }

  private backoff(): number {
    this.failures += 1
    return Math.random() * Math.min(500 * 2 ** (this.failures - 1), 10_000)
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
